import type { LinkFragment, MediaFragment, ProductEntryFragment, ProductExternalEntryFragment, ProductExternalThumbFragment, ProductThumbFragment } from '#graphql-operations'

type ProductEntryLike
  = | ProductThumbFragment
    | ProductEntryFragment
    | ProductExternalThumbFragment
    | ProductExternalEntryFragment

export interface ResolvedProduct {
  id?: string | number
  title?: string
  url?: string
  slug?: string
  handle?: string
  brand?: string
  heading?: string
  subheading?: string
  price?: string
  compareAtPrice?: string
  size?: string
  image?: MediaFragment | { url?: string, alt?: string, width?: number, height?: number }
  isExternal: boolean
  additionalInformations?: { title: string, text: string }[]
  links?: LinkFragment[]
}

function getMetafield(metafields: ({ key?: string, value?: string } | null)[] | undefined, key: string) {
  return metafields?.find(metafield => metafield?.key === key)?.value ?? undefined
}

export function useProduct(entry: MaybeRefOrGetter<ProductEntryLike | undefined | null>) {
  const { public: { shopifyStoreUrl, shopifyCurrency } } = useRuntimeConfig()
  const { locale } = useI18n()

  return computed<ResolvedProduct | undefined>(() => {
    const value = toValue(entry)
    if (!value)
      return undefined

    if (value.__typename === 'productExternal_Entry') {
      return {
        id: value.id,
        title: value.title,
        url: value.url,
        slug: value.slug,
        brand: value.brand,
        heading: value.brand,
        subheading: value.title,
        price: value.price,
        size: value.size,
        image: value.thumbMedia?.[0] ?? undefined,
        isExternal: true,
        links: 'links' in value
          ? value.links?.map(link => link?.customLink).filter((link): link is LinkFragment => !!link)
          : undefined,
        additionalInformations: 'productInformations' in value
          ? value.productInformations
              ?.filter((info): info is { key: string, value: string } => !!info?.key && !!info?.value)
              .map(info => ({ title: info.key, text: info.value }))
          : undefined,
      }
    }

    const shopifyProduct = value.product?.[0]
    const variant = shopifyProduct?.variants?.find(variant => variant?.price) ?? shopifyProduct?.variants?.[0]
    const shopifyImage = shopifyProduct?.images?.find(image => image?.image?.url)?.image
      ?? shopifyProduct?.variants?.find(variant => variant?.image?.url)?.image
    const metafields = shopifyProduct?.metafields

    return {
      id: shopifyProduct?.id ?? value.id,
      title: shopifyProduct?.title ?? value.title,
      url: shopifyProduct?.onlineStoreUrl ?? (shopifyProduct?.handle ? `${shopifyStoreUrl}/products/${shopifyProduct.handle}` : undefined),
      slug: shopifyProduct?.slug ?? value.slug,
      handle: shopifyProduct?.handle,
      brand: shopifyProduct?.vendor,
      heading: getMetafield(metafields, 'title') ?? shopifyProduct?.title ?? value.title,
      subheading: getMetafield(metafields, 'short'),
      price: formatMoney(variant?.price, shopifyCurrency, locale.value),
      compareAtPrice: formatMoney(variant?.compareAtPrice, shopifyCurrency, locale.value),
      size: getMetafield(metafields, 'size'),
      image: shopifyImage
        ? {
            url: shopifyImage.url?.split('?')[0],
            alt: shopifyImage.altText || shopifyProduct?.title || value.title,
            width: shopifyImage.width,
            height: shopifyImage.height,
          }
        : undefined,
      isExternal: true,
    }
  })
}
